import React from 'react'

import {
  BenefitsHomePageDocument,
  CreateBenefitsHealthcarePlanDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { convertDate } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { closeModal } from 'modules/benefits/components/benefitModals/actions'
import { useBenefitModals } from 'modules/benefits/components/benefitModals/useBenefitModals'

import HealthcarePlanWizard from './index'
import { HealthcarePlanData } from './types'
import { healthcarePlanWizardSchema } from './validation'

const HealthcarePlanWizardContainer: React.FC = () => {
  const { dispatch } = useBenefitModals()

  const {
    company: { id: companyId },
    access: { locale },
    employee,
  } = useCurrentSession()

  const [createHealthcarePlan] = useMutation(CreateBenefitsHealthcarePlanDocument, {
    extractorFn: (data) => data.benefitsMutations.createBenefitsHealthcarePlan,
    refetchQueries: [{ query: BenefitsHomePageDocument, variables: { companyId } }],
  })

  const currency = employee?.legalEntity?.currency ?? 'EUR'

  const handleSubmit = async (data: HealthcarePlanData) => {
    //This is built from each step schema so it can't fail
    const validationResult = healthcarePlanWizardSchema(locale, currency).parse(data)

    const result = await createHealthcarePlan({
      variables: {
        companyId: data.companyId,
        coveredDependentCostCents: validationResult.allowDependents
          ? validationResult.coveredDependentCostCents
          : undefined,
        coveredEmployeeCostCents: validationResult.coveredEmployeeCostCents,
        dependentCostCents: validationResult.allowDependents
          ? validationResult.dependentCostCents
          : undefined,
        employeeCostCents: validationResult.employeeCostCents,
        planDocument: validationResult.planDocument,
        planName: validationResult.planName,
        providerName: validationResult.providerName,
        renewalDate: convertDate(validationResult.renewalDate, 'ui', 'backend'),
        waitingPeriodMonths: validationResult.waitingPeriodMonths,
        allowPreExistingConditions: validationResult.allowPreExistingConditions,
      },
    })

    const handled = handleResult(result, (errors) => {
      const errorMessages = Object.values(errors)
      if (errorMessages.length > 0) {
        app.setFlash({
          type: 'wrong',
          message: errorMessages.join(', '),
          time: 5000,
        })
      }
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('benefits.healthcare.plan_wizard.create_success'),
        time: 3000,
      })
      dispatch(closeModal())
    }
  }

  return (
    <HealthcarePlanWizard
      onSubmit={handleSubmit}
      onClose={() => dispatch(closeModal())}
      locale={locale}
      currency={currency}
    />
  )
}

export default HealthcarePlanWizardContainer
